import { useEffect, useState } from "react";
import { tr } from "../i18n/tr";
import { Loader2, BookOpen, CheckCircle2, Lock, Package } from "lucide-react";
import { toast } from "sonner";
import { BlockRenderer, THEMES } from "../pages/formation/BlockEditor";
import { ScormPlayer } from "./lms/ScormPlayer";
import { ScormOfflineButton } from "./offline/ScormOfflineButton";

/**
 * Renders the course content of a sequence (module blocks) for a student or
 * trainer. Shows a lock screen when the sequence isn't unlocked yet.
 *
 * Props:
 *  - api: axios instance (from useAuth.axiosAuth())
 *  - sessionId, sequence { sequence_id, title, module_id? }
 *  - enrollmentId: inscription de l'élève (tracking SCORM)
 *  - unlocked: boolean
 *  - done: boolean
 *  - onComplete(): called after marking the sequence as done
 */
export default function CourseModuleViewer({ api, sessionId, sequence, enrollmentId, unlocked, done, onComplete }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizScore, setQuizScore] = useState(null);

  useEffect(() => {
    if (!sequence?.sequence_id) return;
    setLoading(true);
    api.get(`/student/sessions/${sessionId}/sequences/${sequence.sequence_id}/content`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.offline ? "Module non disponible hors ligne" : (e.response?.data?.detail || e.message)))
      .finally(() => setLoading(false));
  // eslint-disable-next-line
  }, [sequence?.sequence_id, sessionId]);

  const markDone = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/student/sessions/${sessionId}/complete-sequence`, { sequence_id: sequence.sequence_id });
      toast.success(r?.queued ? "Etape validee (hors ligne) — synchronisation au retour du reseau." : tr("lms.etape_validee_0504", "Etape validee !"));
      onComplete && onComplete({ queued: !!r?.queued });
    } catch (e) {
      if (e.response?.status === 423) toast.error(tr("lms.le_formateur_n_a_ed04", "Le formateur n'a pas encore deverrouille cette etape."));
      else toast.error(e.response?.data?.detail || e.message);
    }
    setSubmitting(false);
  };

  if (!sequence) {
    return (
      <div className="py-16 text-center text-slate-500">
        <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-40" />
        <div className="text-sm">{tr("lms.selectionnez_un_module_dans_2e5b", "Selectionnez un module dans la liste a gauche.")}</div>
      </div>
    );
  }

  if (loading) {
    return <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[var(--brand-primary,#f7c935)]" /></div>;
  }

  const theme = THEMES[data?.module?.theme || "blitzz"] || THEMES.blitzz;
  const blocks = data?.blocks || [];
  const quiz = data?.quiz;
  const isScorm = data?.module?.kind === "scorm";

  if (!unlocked) {
    return (
      <div className="py-16 text-center border border-dashed border-white/10 rounded-xl bg-[#14101c]" data-testid="course-module-locked">
        <Lock className="w-12 h-12 mx-auto mb-3 text-slate-600" />
        <div className="font-bold text-white mb-1">{tr("lms.module_en_attente_du_bfb8", "Module en attente du formateur")}</div>
        <p className="text-xs text-slate-500 max-w-sm mx-auto">
          {tr("lms.cette_etape_sera_accessible_9549", "Cette etape sera accessible lorsque votre formateur aura deverrouille la sequence. Progression controlee en direct.")}
        </p>
      </div>
    );
  }

  if (isScorm) {
    const m = data.module;
    return (
      <div data-testid="course-module-viewer">
        <div className="mb-5 pb-5 border-b border-white/10">
          <div className="text-[10px] uppercase tracking-wider text-sky-400 font-bold mb-1 flex items-center gap-1"><Package className="w-3 h-3" /> Module interactif SCORM {m.scorm_version || ""}
            <span className="ml-auto">{api?.patchCache && <ScormOfflineButton api={api} contentId={m.module_id} />}</span>
          </div>
          <h2 className="text-2xl font-black text-white">{m.title || sequence.title}</h2>
          {m.description && <p className="text-sm text-slate-400 mt-1">{m.description}</p>}
        </div>
        <ScormPlayer
          sequence={{ sequence_id: m.module_id, scorm_entry: m.scorm_entry, scorm_version: m.scorm_version, title: m.title }}
          enrollmentId={enrollmentId || data.enrollment_id}
          api={api}
          onCompleted={onComplete}
        />
        <div className="mt-6 flex items-center justify-end gap-3">
          {done ? (
            <span className="flex items-center gap-1 text-xs px-3 py-1.5 rounded bg-green-500/15 text-green-400 font-bold uppercase" data-testid="module-done-badge">
              <CheckCircle2 className="w-3.5 h-3.5" /> {tr("lms.termine_ad1f", "Termine")}
            </span>
          ) : (
            <span className="text-[11px] text-slate-500" data-testid="scorm-auto-complete-hint">{tr("lms.la_validation_est_automatique_05c8", "La validation est automatique une fois le module terminé.")}</span>
          )}
        </div>
      </div>
    );
  }

  const submitQuiz = () => {
    if (!quiz?.questions) return;
    let correct = 0;
    (quiz.questions || []).forEach((q) => {
      const ans = quizAnswers[q.question_id || q.id];
      if (q.correct === ans || (Array.isArray(q.correct) && q.correct.includes(ans))) correct += 1;
    });
    const total = Math.max(1, (quiz.questions || []).length);
    const pct = Math.round((100 * correct) / total);
    setQuizScore(pct);
    toast.success(`Score: ${pct}% (${correct}/${total})`);
  };

  return (
    <div data-testid="course-module-viewer">
      <div className="mb-5 pb-5 border-b border-white/10">
        <div className="text-[10px] uppercase tracking-wider text-[var(--brand-primary,#f7c935)] font-bold mb-1">{tr("lms.module_en_cours_3b7b", "Module en cours")}</div>
        <h2 className="text-2xl font-black text-white">{data?.module?.title || sequence.title}</h2>
        {data?.module?.description && <p className="text-sm text-slate-400 mt-1">{data.module.description}</p>}
      </div>

      {blocks.length === 0 && !quiz && (
        <div className="py-12 text-center text-slate-500 text-sm">{tr("lms.ce_module_ne_contient_b72a", "Ce module ne contient pas encore de contenu.")}</div>
      )}

      <div className="space-y-5 prose-invert" style={{ color: theme.text }}>
        {blocks.map((b, i) => (
          <div key={i}><BlockRenderer block={b} theme={theme} /></div>
        ))}
      </div>

      {quiz && (quiz.questions || []).length > 0 && (
        <div className="mt-8 bg-[#14101c] border border-white/10 rounded-xl p-5" data-testid="module-quiz">
          <h3 className="text-lg font-bold text-white mb-3">{tr("lms.quiz_de_validation_fdd0", "Quiz de validation")}</h3>
          <div className="space-y-4">
            {(quiz.questions || []).map((q, qi) => {
              const qid = q.question_id || q.id || `q${qi}`;
              return (
                <div key={qid}>
                  <div className="text-sm font-semibold text-white mb-1">{qi + 1}. {q.text || q.title}</div>
                  <div className="space-y-1">
                    {(q.choices || q.options || []).map((c, ci) => (
                      <label key={ci} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer hover:bg-white/5 rounded px-2 py-1">
                        <input
                          type="radio"
                          name={qid}
                          checked={quizAnswers[qid] === (c.value || ci)}
                          onChange={() => setQuizAnswers({ ...quizAnswers, [qid]: c.value || ci })}
                          data-testid={`quiz-${qid}-${ci}`}
                        />
                        <span>{c.label || c.text || c}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex items-center justify-between">
            <button
              onClick={submitQuiz}
              className="bg-[var(--brand-primary,#f7c935)] text-[#14101c] font-bold text-xs px-4 py-2 rounded-lg"
              data-testid="quiz-submit-btn"
            >
              {tr("lms.valider_mes_reponses_16d8", "Valider mes reponses")}
            </button>
            {quizScore !== null && (
              <div className={"text-sm font-bold " + (quizScore >= 70 ? "text-green-400" : "text-amber-400")}>
                Score: {quizScore}%
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-8 flex items-center justify-end gap-3">
        {done ? (
          <span className="flex items-center gap-1 text-xs px-3 py-1.5 rounded bg-green-500/15 text-green-400 font-bold uppercase" data-testid="module-done-badge">
            <CheckCircle2 className="w-3.5 h-3.5" /> {tr("lms.termine_ad1f", "Termine")}
          </span>
        ) : (
          <button
            onClick={markDone}
            disabled={submitting}
            className="bg-[var(--brand-primary,#f7c935)] text-[#14101c] font-bold text-sm px-6 py-2 rounded-lg disabled:opacity-50"
            data-testid="module-complete-btn"
          >
            {submitting ? "Validation..." : tr("lms.marquer_comme_termine_79c9", "Marquer comme termine")}
          </button>
        )}
      </div>
    </div>
  );
}
